import { Button, ChevronRight } from '@chain-zen/ui'
import Image from 'next/image'

import { apps as allAppsData } from '../../../../../../../components/apps/apps-data'
import type { IELDApp } from '../../../../../../../data/queries/eld-apps'

/**
 * Gets the dark mode image URL for an ELD app based on its type
 */
const getAppImageURL = (appType: string): string | null => {
  const app = allAppsData.find((a) => a.appType === appType)
  return app?.darkImageURL ?? null
}

interface IELDAppItemProps {
  app: IELDApp
  disabled?: boolean
  onSelect: (app: IELDApp) => void
}

/**
 * Individual ELD app button showing app icon, name, and connection status.
 */
export const ELDAppItem = ({ app, disabled = false, onSelect }: IELDAppItemProps) => {
  const imageURL = getAppImageURL(app.appType)

  return (
    <Button
      onClick={() => onSelect(app)}
      disabled={disabled}
      variant="outline"
      className={`w-full h-auto py-2.5 px-3 justify-start border-border hover:border-emerald-500/50 hover:bg-accent/50 transition-all flex items-center gap-3 group cursor-pointer text-left shrink-0 ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {/* App Icon */}
      <div className="w-8 h-8 rounded-md bg-muted border border-border flex items-center justify-center shrink-0 overflow-hidden">
        {imageURL ? (
          <Image src={imageURL} alt={app.appType} width={24} height={24} className="object-contain" />
        ) : (
          <span className="text-sm">📊</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-foreground text-sm font-medium">{app.appType}</div>
        <div className="text-xs text-emerald-400 flex items-center gap-1">
          <span>●</span>
          <span>Connected</span>
          {app.sourceWorkspaceType === 'carrier' && (
            <>
              <span className="text-muted-foreground/50">•</span>
              <span className="text-muted-foreground">from carrier</span>
            </>
          )}
        </div>
        {!!app.workspaceDisplayName && (
          <div className="text-xs text-muted-foreground truncate mt-0.5">{app.workspaceDisplayName}</div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-400 shrink-0" />
    </Button>
  )
}
